import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { RefreshCw, Plus, Shield, Users as UsersIcon, Lock, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../../auth/AuthContext";
import { useDisclosure } from "@heroui/react";
import { fetchUsersAccess, type UserAccessItem, type AccessKpis } from "../../../api/accessApi";
import type { AccessRequestItem } from "../../../api/accessRequestsApi";

import KpiCards from "./KpiCards";
import UsersTable from "./UsersTable";
import UserDrawer from "./UserDrawer";
import CreateUserModal from "./CreateUserModal";
import PermissionMatrix from "./PermissionMatrix";
import AccessRequestsTable from "./AccessRequestsTable";
import AccessRequestDrawer from "./AccessRequestDrawer";
import "../../../styles/access-page.css";

export default function AccessPage() {
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL Tab Sync: ?tab=users | roles | requests
  const tabParam = searchParams.get("tab");
  const activeTab: "users" | "roles" | "requests" =
    tabParam === "roles" ? "roles" : tabParam === "requests" ? "requests" : "users";

  const handleTabChange = (tab: "users" | "roles" | "requests") => {
    setSearchParams({ tab });
  };

  // User Directory State
  const [users, setUsers] = useState<UserAccessItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Table filters & pagination state
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [search, setSearch] = useState<string>("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [planFilter, setPlanFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<string>("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Modals and Drawer state
  const { isOpen: isCreateOpen, onOpen: onOpenCreate, onOpenChange: onOpenChangeCreate } = useDisclosure();
  const [selectedDrawerUser, setSelectedDrawerUser] = useState<UserAccessItem | null>(null);
  const [selectedDrawerRequest, setSelectedDrawerRequest] = useState<AccessRequestItem | null>(null);
  const [pendingRequestsCount, setPendingRequestsCount] = useState<number>(0);

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Mount & Dependency Data Fetching
  const loadUsers = async () => {
    try {
      setIsLoading(true);
      setErrorMsg(null);
      const res = await fetchUsersAccess({
        search: search || undefined,
        role: roleFilter,
        status: statusFilter,
        plan: planFilter,
        skip: (page - 1) * pageSize,
        limit: pageSize,
        sort: sortField,
        direction: sortDirection,
      });

      const rawItems = res?.items || (Array.isArray(res) ? res : []);
      const mappedUsers = rawItems.map((u: any) => ({
        ...u,
        id: u.id || u._id,
        _id: u._id || u.id,
        role: u.role || "client",
        status: u.status || (u.is_active ? "active" : "suspended"),
        is_active: u.is_active ?? (u.status === "active"),
      }));

      setUsers(mappedUsers);
      setTotal(res?.total ?? mappedUsers.length);
    } catch (err: unknown) {
      console.error("Failed to load access users:", err);
      const msg = err instanceof Error ? err.message : "Failed to load user directory";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [page, pageSize, search, roleFilter, statusFilter, planFilter, sortField, sortDirection]);

  // Compute KPI summary metrics from fetched state
  const kpis: AccessKpis = useMemo(() => {
    const total_accounts = total || users.length;
    const clients_count = users.filter((u) => u.role?.toLowerCase() === "client").length;
    const analysts_admins_count = users.filter(
      (u) =>
        u.role?.toLowerCase() === "analyst" ||
        u.role?.toLowerCase() === "admin" ||
        u.role?.toLowerCase() === "super_admin"
    ).length;
    const suspended_pending_count = users.filter(
      (u) => u.status === "suspended" || u.status === "pending" || !u.is_active
    ).length;

    return {
      total_accounts,
      clients_count,
      analysts_admins_count,
      suspended_pending_count,
    };
  }, [users, total]);

  const isSuperAdmin =
    currentUser?.role === "super_admin" ||
    currentUser?.email === "superanalyst@threatactix.com" ||
    localStorage.getItem("role") === "super_admin";

  return (
    <div className="access-page-container">
      {/* Header */}
      <div className="access-header">
        <div className="access-header-left">
          <div className="access-breadcrumb">
            <span>Admin</span> &gt; <span>Access</span>
          </div>
          <div className="access-title-wrap">
            <h1 className="access-title">Access Management</h1>
            {isSuperAdmin && (
              <span className="super-admin-badge">
                <Shield size={12} /> Super Admin Mode
              </span>
            )}
          </div>
          <p className="access-subtitle">Users, clients, roles and permissions</p>
        </div>

        <div className="access-header-actions">
          <button className="btn-secondary" onClick={() => loadUsers()} title="Refresh directory">
            <RefreshCw size={15} /> Refresh
          </button>
          <button className="btn-primary" onClick={onOpenCreate} id="btn-create-user">
            <Plus size={16} /> Create User
          </button>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <KpiCards kpis={kpis} isLoading={isLoading} />

      {/* Navigation Tabs */}
      <div className="access-tabs-bar">
        <button
          className={`access-tab-btn${activeTab === "users" ? " active" : ""}`}
          onClick={() => handleTabChange("users")}
          id="tab-users-clients"
        >
          <UsersIcon size={16} /> Users & Clients
        </button>
        <button
          className={`access-tab-btn${activeTab === "roles" ? " active" : ""}`}
          onClick={() => handleTabChange("roles")}
          id="tab-roles-permissions"
        >
          <Lock size={16} /> Roles & Permissions
        </button>
        <button
          className={`access-tab-btn${activeTab === "requests" ? " active" : ""}`}
          onClick={() => handleTabChange("requests")}
          id="tab-access-requests"
        >
          <UserCheck size={16} /> Access Requests
          {pendingRequestsCount > 0 && (
            <span
              style={{
                marginLeft: 6,
                background: "#f59e0b",
                color: "#000",
                fontSize: 10,
                fontWeight: 800,
                padding: "2px 6px",
                borderRadius: 9999,
              }}
            >
              {pendingRequestsCount}
            </span>
          )}
        </button>
      </div>

      {/* Tab View */}
      {errorMsg ? (
        <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 12, padding: 32, margin: "20px 0", textAlign: "center", color: "#ef4444" }}>
          <h3 style={{ margin: "0 0 8px 0", fontSize: "1.1rem", fontWeight: 700 }}>Failed to load directory data</h3>
          <p style={{ margin: "0 0 16px 0", fontSize: "0.85rem", opacity: 0.8 }}>{errorMsg}</p>
          <button className="btn-primary" onClick={() => loadUsers()} style={{ margin: "0 auto" }}>
            <RefreshCw size={14} /> Retry Loading
          </button>
        </div>
      ) : activeTab === "requests" ? (
        <AccessRequestsTable
          onSelectRequest={(req) => setSelectedDrawerRequest(req)}
          onCountsUpdated={(counts) => setPendingRequestsCount(counts.pending)}
        />
      ) : activeTab === "users" ? (
        <UsersTable
          users={users}
          total={total}
          isLoading={isLoading}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          search={search}
          setSearch={setSearch}
          roleFilter={roleFilter}
          setRoleFilter={setRoleFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          planFilter={planFilter}
          setPlanFilter={setPlanFilter}
          sortField={sortField}
          setSortField={setSortField}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          onManageUser={(u) => setSelectedDrawerUser(u)}
          onRefresh={loadUsers}
          currentAdminEmail={currentUser?.email}
        />
      ) : (
        <PermissionMatrix />
      )}

      {/* Right Drawer for User Profile & Custom Perm Overrides */}
      <UserDrawer
        user={selectedDrawerUser}
        isOpen={Boolean(selectedDrawerUser)}
        onClose={() => setSelectedDrawerUser(null)}
        onUserUpdated={loadUsers}
        currentAdminEmail={currentUser?.email}
      />

      {/* Right Drawer for Access Request Details & Decision */}
      <AccessRequestDrawer
        request={selectedDrawerRequest}
        isOpen={Boolean(selectedDrawerRequest)}
        onClose={() => setSelectedDrawerRequest(null)}
        onRequestUpdated={() => {
          // Handled inside requests table
        }}
      />

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={isCreateOpen}
        onOpenChange={onOpenChangeCreate}
        onUserCreated={loadUsers}
      />
    </div>
  );
}
